import React, { useEffect, useMemo, useState } from 'react';
import { translate } from '../../Services/translation';
import { query as queryOrders } from '../../Actions/Order.action';
import { useDispatch } from 'react-redux';
import OrderListItemMobile from './ui/OrderListItemMobile';
import OrderListItemDesktop from './ui/OrderListItemDesktop';
import { calculatePager, Pager } from '../../Services/Pagination.service';
import Pagination from '../Pagination';
import { setOrder } from '../../Actions/Order.action';
import { Order } from '../../Types/Order';
import { useNavigate } from 'react-router-dom';
import SkeletonListItemDesktop from './ui/SkeletonListItemDesktop';
import SkeletonListItemMobile from './ui/SkeletonListItemMobile';
import {
  ExtapiOrderSearchQueryParams,
  ExtapiOrderSearchResult,
} from '../../Types/MyPages/OrderModel';
import { getOrders } from '../../Actions/Invoices.action';

const MyPagesOrderHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageSize: number = 10;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [orders, setOrders] = useState<ExtapiOrderSearchResult>();
  const [pager, setPager] = useState<Pager>({});
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [showOnlyMyOrders, setShowOnlyMyOrders] = useState(false);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const queryParams: ExtapiOrderSearchQueryParams = {
        pageIndex: currentPageIndex,
        pageSize: pageSize,
      };
      const data = await getOrders(queryParams);
      setOrders(data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageIndex]);

  useEffect(() => setCurrentPageIndex(1), [showOnlyMyOrders]);

  useEffect(() => {
    setPager(calculatePager(orders?.hits, currentPageIndex, {}, pageSize));
  }, [orders, currentPageIndex]);

  const onOrderClick = (order: Order) => {
    dispatch(setOrder(order));
    navigate(order.externalOrderID);
  };

  return (
    <div className="col-span-3">
      <h1 className="mb-2">{translate('mypage.order.title')}</h1>
      <p className="mb-4 max-w-3xl text-lg lg:mb-6">
        {translate('mypage.order.text') !== 'mypage.order.text' &&
          translate('mypage.order.text')}
      </p>
      <ul className="lg:hidden">
        <SkeletonListItemMobile items={3} cells={5} loading={isLoading} />
        {!isLoading &&
          orders &&
          orders.result &&
          orders.result.map((order) => (
            <OrderListItemMobile
              order={order}
              key={order.externalOrderID + 'mobile'}
              onClick={onOrderClick}
            />
          ))}
      </ul>
      <ul className="hidden rounded border border-solid border-gray-300 lg:block">
        <li className="grid grid-cols-6 gap-4 rounded-t border-b border-solid border-gray-300 bg-white p-2">
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
            {translate('general.ordernumber')}
          </p>
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
            {translate('orderdetail.yourordernumber')}
          </p>
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
            {translate('general.quantity')}
          </p>
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
            {translate('orderdetail.orderdate')}
          </p>
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-right text-sm font-bold">
            {translate('orderdetail.grandtotal')}
          </p>
        </li>
        <SkeletonListItemDesktop items={5} cells={6} loading={isLoading} />
        {!isLoading &&
          orders?.result?.map((order) => (
            <OrderListItemDesktop
              order={order}
              key={order.externalOrderID + 'desktop'}
              onClick={onOrderClick}
            />
          ))}
      </ul>
      <div className="mt-4">
        <Pagination
          model={pager}
          onChange={(index) =>
            index !== currentPageIndex && setCurrentPageIndex(index)
          }
        />
      </div>
    </div>
  );
};

export default MyPagesOrderHistory;
